<template>
  <SatellitesPage>
    <template v-if="routers.length" #actions>
      <button class="btn _bg-green sat-btn-inline" :disabled="limitReached || opening" @click.prevent="openEditor()"><SatIcon name="plus" /> {{ $t("satellites.routers.create") }}</button>
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <template v-else>
      <div class="routers-head">
        <div>
          <p class="routers-description">{{ $t("satellites.routers.description") }}</p>
          <div class="routers-counter" :class="{ _full: limitReached }">
            {{ $t("satellites.routers.counter", { count, limit }) }}
          </div>
        </div>
      </div>
      <p v-if="limitReached && routers.length" class="routers-limit-note">{{ $t("satellites.routers.limit_reached", { limit }) }}</p>

      <div v-if="!routers.length" class="routers-empty">
        <svg v-svg symbol="file" size="0 0 25 32" role="presentation" class="routers-empty__icon" />
        <span class="routers-empty__title">{{ $t("satellites.routers.empty_title") }}</span>
        <span class="routers-empty__text">{{ $t("satellites.routers.empty_description") }}</span>
        <span v-if="options && !options.landings.length" class="routers-empty__text">
          {{ $t("satellites.routers.no_landings") }}
          <router-link :to="{ name: 'SatelliteLandings' }">{{ $t("satellites.routers.go_landings") }}</router-link>
        </span>
        <button class="btn _bg-green mt-2" :disabled="limitReached || opening" @click.prevent="openEditor()">{{ $t("satellites.routers.create") }}</button>
      </div>

      <div v-else class="c-table mt-4">
        <div class="inner">
          <vue-good-table styleClass="table  _no-wrap" :columns="columns" :rows="routers">
            <template slot="table-column" slot-scope="props">
              <span v-show="props.column.field !== 'control'">{{ $t(props.column.label) }}</span>
            </template>

            <template slot="table-row" slot-scope="props">
              <template v-if="props.column.field === 'name'">
                <a href="#" class="routers-name" @click.prevent="openEditor(props.row)">{{ props.row.name }}</a>
              </template>
              <template v-else-if="props.column.field === 'routes'">
                <div class="routers-paths">
                  <span v-for="route in visibleRoutes(props.row)" :key="route.path" class="routers-paths__item" v-tooltip="{ content: routeSummary(route), triggers: ['hover', 'click'] }">
                    {{ route.path }}
                  </span>
                  <span v-if="hiddenRoutesCount(props.row)" class="routers-muted">+{{ hiddenRoutesCount(props.row) }}</span>
                </div>
              </template>
              <template v-else-if="['created_at', 'updated_at'].includes(props.column.field)">
                <span v-if="props.row[props.column.field]">{{ formatDate(props.row[props.column.field]) }}</span>
                <span v-else class="routers-muted">—</span>
              </template>
              <template v-else-if="props.column.field === 'used_by'">
                <router-link v-if="domainsOf(props.row).length" :to="{ name: 'SatelliteDomains', query: { router: props.row.id } }" class="routers-domains">
                  <span v-for="domain in domainsOf(props.row).slice(0, 2)" :key="domain.id" class="sat-chip">{{ domain.host }}</span>
                  <span v-if="domainsOf(props.row).length > 2" class="sat-chip _accent">+{{ domainsOf(props.row).length - 2 }}</span>
                </router-link>
                <span v-else class="routers-muted">{{ $t("satellites.routers.not_used") }}</span>
              </template>
              <template v-else-if="props.column.field === 'control'">
                <div class="controls-wrapper">
                  <router-link :to="{ name: 'SatelliteLinks', query: { router: props.row.id } }" class="sat-icon-btn" :class="{ _disabled: !domainsOf(props.row).length }" :title="$t('satellites.links.open')">
                    <SatIcon name="link" />
                  </router-link>
                  <button @click="openEditor(props.row)" class="sat-icon-btn" :disabled="opening" :title="$t('satellites.routers.edit')">
                    <SatIcon name="edit" />
                  </button>
                  <button @click="askDelete(props.row)" class="sat-icon-btn _danger" :title="$t('satellites.delete')">
                    <SatIcon name="trash" />
                  </button>
                </div>
              </template>
              <template v-else>{{ props.formattedRow[props.column.field] }}</template>
            </template>
            <div class="d-flex justify-content-center" slot="emptystate">
              {{ $t("satellites.routers.empty_title") }}
            </div>
          </vue-good-table>
        </div>
      </div>
    </template>

    <!-- Creation of a new router or editing of an existing one (target) -->
    <modal @closed="closeEditor" :width="900" name="satelliteRouterEditor" height="auto" :scrollable="true" classes="creative-modal overflow-removed">
      <div class="pt-3 pb-4 px-4">
        <svg v-svg @click="$modal.hide('satelliteRouterEditor')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">
          {{ target ? $t("satellites.routers.edit_title", { name: target.name }) : $t("satellites.routers.create_title") }}
        </h3>
        <RouterEditor
          v-if="options"
          class="mt-3"
          :router="target"
          :options="options"
          :violations="violations"
          :error="editorError"
          :saving="saving"
          @save="save"
          @cancel="$modal.hide('satelliteRouterEditor')"
        >
          <template #extra>
            <div class="routers-domains-field">
              <div class="sat-field__label">{{ $t("satellites.routers.domains_label") }}</div>
              <DomainPicker v-model="domainIds" :domains="domains" :router-id="target ? target.id : null" />
              <div class="sat-field__hint">{{ $t("satellites.routers.domains_hint") }}</div>
            </div>
          </template>
        </RouterEditor>
      </div>
    </modal>

    <modal @closed="closeDelete" :width="550" name="satelliteRouterDelete" height="auto" :scrollable="true" classes="creative-modal">
      <div class="pt-3 pb-4 px-3 mx-auto d-block text-center">
        <svg v-svg @click="$modal.hide('satelliteRouterDelete')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">{{ $t("satellites.routers.delete_confirm", { name: target ? target.name : "" }) }}</h3>
        <p class="pt-2 routers-muted">{{ $t("satellites.routers.delete_note") }}</p>
        <div v-if="target && domainsOf(target).length" class="sat-note _warn text-left mt-2">
          <SatIcon name="alert" />
          <span>{{ $t("satellites.routers.delete_domains", { hosts: domainsOf(target).map((d) => d.host).join(", ") }) }}</span>
        </div>
        <button class="btn mt-4 mr-3 _bg-gray" @click="$modal.hide('satelliteRouterDelete')">{{ $t("cancel") }}</button>
        <button class="btn mt-4 _bg-green" :disabled="saving" @click="remove">{{ $t("satellites.delete") }}</button>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import RouterEditor from "@/components/Satellites/RouterEditor.vue";
import DomainPicker from "@/components/Satellites/DomainPicker.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk } from "@/components/Satellites/format";

// Paths shown in the list, the rest is a "+N" counter
const VISIBLE_ROUTES = 5;

export default {
  name: "SatelliteRouters",
  components: { SatellitesPage, SatIcon, RouterEditor, DomainPicker },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  data() {
    return {
      loading: true,
      routers: [],
      count: 0,
      limit: 0,
      // data of /satellite-routers/options: { platforms, landings, defaults, limits }
      options: null,
      // Options are reloaded before the editor opens
      opening: false,
      saving: false,
      // Router being edited or deleted
      target: null,
      violations: {},
      editorError: "",
      // All domains of the user, for the domains of the edited router
      domains: [],
      domainIds: [],
      columns: [
        { label: "satellites.routers.columns.name", field: "name", sortable: false },
        { label: "satellites.routers.columns.routes", field: "routes", sortable: false },
        { label: "satellites.routers.columns.used_by", field: "used_by", sortable: false },
        { label: "satellites.routers.columns.updated_at", field: "updated_at", sortable: false },
        { label: "", field: "control", sortable: false },
      ],
    };
  },
  computed: {
    limitReached() {
      return this.limit > 0 && this.count >= this.limit;
    },
  },
  mounted() {
    Promise.all([this.fetch(), this.loadOptions(), this.loadDomains()]).then(() => this.openFromQuery());
  },
  methods: {
    domainsOf(router) {
      return (router.used_by && router.used_by.domains) || [];
    },
    loadDomains() {
      return satelliteService.getDomains().then((res) => {
        if (isOk(res)) this.domains = (res.data && res.data.domains) || [];
      });
    },
    visibleRoutes(router) {
      return (router.routes || []).slice(0, VISIBLE_ROUTES);
    },
    hiddenRoutesCount(router) {
      return Math.max((router.routes || []).length - VISIBLE_ROUTES, 0);
    },
    // Landings of a route for the tooltip of its path
    routeSummary(route) {
      const landings = (this.options && this.options.landings) || [];
      const names = (route.landing_ids || []).map((id) => {
        const landing = landings.find((option) => option.id === id);
        return landing ? landing.name : this.$t("satellites.routers.unknown_landing");
      });
      return `${route.path} → ${names.join(", ")}`;
    },
    fetch() {
      return satelliteService
        .getRouters()
        .then((res) => {
          if (isOk(res)) {
            const data = res.data || {};
            this.routers = Array.isArray(data.routers) ? data.routers : [];
            this.count = Number.isInteger(data.count) ? data.count : this.routers.length;
            this.limit = Number.isInteger(data.limit) ? data.limit : 0;
          } else if (res && res.status === 403) {
            this.$router.replace({ name: "Dashboard" }).catch(() => {});
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Resolves with true if the options are loaded.
    loadOptions() {
      return satelliteService.getRouterOptions().then((res) => {
        if (isOk(res) && res.data) {
          this.options = {
            ...res.data,
            platforms: Array.isArray(res.data.platforms) ? res.data.platforms : [],
            landings: Array.isArray(res.data.landings) ? res.data.landings : [],
          };
          return true;
        }
        if (!res || res.status !== 403) this.showError(res);
        return false;
      });
    },
    // The landings page links to a router by ?router=<id>: its editor opens right away.
    // ?create=1 (from the router menus of the domains) opens a new router.
    openFromQuery() {
      const { router: id, create } = this.$route.query;
      if (!id && !create) return;
      this.$router.replace({ query: {} }).catch(() => {});
      if (create && !this.limitReached && this.options) {
        this.showEditor(null);
        return;
      }
      const router = this.routers.find((item) => item.id === id);
      if (router && this.options) this.showEditor(router);
    },

    // Create / edit
    openEditor(router = null) {
      if ((!router && this.limitReached) || this.opening) return;
      // Fresh platforms and landings: they could have been added since the page was opened
      this.opening = true;
      Promise.all([this.loadOptions(), this.loadDomains()])
        .then(([loaded]) => {
          if (loaded) this.showEditor(router);
        })
        .finally(() => {
          this.opening = false;
        });
    },
    showEditor(router) {
      this.target = router;
      this.domainIds = router ? this.domainsOf(router).map((d) => d.id) : [];
      this.violations = {};
      this.editorError = "";
      this.$modal.show("satelliteRouterEditor");
    },
    closeEditor() {
      this.target = null;
      this.violations = {};
      this.editorError = "";
    },
    save(body) {
      if (this.saving) return;
      this.saving = true;
      this.violations = {};
      this.editorError = "";
      const target = this.target;
      const request = target ? satelliteService.updateRouter(target.id, body) : satelliteService.createRouter(body);
      request
        .then((res) => (isOk(res) ? this.saveDomains(res.data.router.id, target).then(() => res) : res))
        .then((res) => {
          if (isOk(res)) {
            this.$alert({ type: "success", title: this.$t(target ? "satellites.routers.saved" : "satellites.routers.created") });
            this.$modal.hide("satelliteRouterEditor");
            this.fetch();
          } else if (res && res.status === 422 && res.data && res.data.violations) {
            this.violations = res.data.violations;
            const known = Object.keys(this.violations).every(
              (key) =>
                key === "name" ||
                key === "routes" ||
                key === "system_routes" ||
                /^routes\.\d+\.(path|platform|sub_params|strategy|landing_ids)$/.test(key) ||
                /^system_routes\.\d+\.(path|type)$/.test(key)
            );
            if (!known) this.editorError = res.message || this.$t("server_error");
          } else if (res && res.status === 409) {
            this.editorError = res.message || this.$t("satellites.routers.limit_reached", { limit: this.limit });
            this.fetch();
          } else if (res && res.status === 404) {
            this.editorError = res.message || this.$t("server_error");
            this.fetch();
          } else {
            this.editorError = (res && res.message) || this.$t("server_error");
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },

    // Assigns the picked domains to the saved router and takes the dropped ones off it.
    saveDomains(routerId, target) {
      const before = target ? this.domainsOf(target).map((d) => d.id) : [];
      const added = this.domainIds.filter((id) => !before.includes(id));
      const removed = before.filter((id) => !this.domainIds.includes(id));
      const requests = [];
      if (added.length) requests.push(satelliteService.updateDomains(added, { router_id: routerId }));
      if (removed.length) requests.push(satelliteService.updateDomains(removed, { router_id: null }));
      return Promise.all(requests).then((responses) => {
        const failed = responses.find((res) => !isOk(res));
        if (failed) this.showError(failed);
        if (requests.length) this.loadDomains();
      });
    },

    // Delete
    askDelete(router) {
      this.target = router;
      this.$modal.show("satelliteRouterDelete");
    },
    closeDelete() {
      this.target = null;
    },
    remove() {
      if (!this.target || this.saving) return;
      this.saving = true;
      satelliteService
        .deleteRouter(this.target.id)
        .then((res) => {
          if (isOk(res)) {
            this.$alert({ type: "success", title: this.$t("satellites.routers.deleted") });
            this.$modal.hide("satelliteRouterDelete");
            this.fetch();
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.routers-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 24px;
}

.routers-description {
  max-width: 720px;
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sat-muted);
}

.routers-counter {
  margin-top: 8px;
  font-size: 13px;
  color: var(--sat-muted);

  &._full {
    color: var(--sat-warn);
    font-weight: 600;
  }
}

.routers-limit-note {
  margin-top: 8px;
  font-size: 13px;
  color: var(--sat-warn);
}

.routers-name {
  font-weight: 600;
  color: var(--sat-accent);
  white-space: normal;
  word-break: break-word;
}

.routers-paths {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  max-width: 320px;
  white-space: normal;

  &__item {
    padding: 2px 8px;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: var(--sat-text-2);
    background: var(--sat-surface);
    word-break: break-all;
    cursor: default;
  }
}

.routers-domains {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 320px;

  &:hover {
    text-decoration: none;

    .sat-chip {
      border-color: var(--sat-accent);
    }
  }
}

.routers-domains-field {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--sat-border);
}

.routers-muted {
  color: var(--sat-muted);
  font-size: 13px;
}

.routers-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 60px 20px;
  text-align: center;
  background-color: var(--sat-surface);
  border-radius: 16px;

  &__icon {
    width: 40px;
    height: 52px;
    fill: var(--sat-accent);
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: var(--sat-text);
  }

  &__text {
    max-width: 560px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--sat-text-2);
  }
}

.controls-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;

  .sat-icon-btn._disabled {
    opacity: 0.35;
    pointer-events: none;
  }
}
</style>
